import React, { useEffect, useState } from 'react';
import type {
  EvaluationLevelDisplayLabelsByStepCount,
  EvaluationLevelStepCount,
  WorkspaceEvaluationPlanLevelSettings,
} from '../../types/evaluationPlan';
import {
  LEVEL_STEP_COUNT_OPTIONS,
  clampLevelStepCount,
  emptyWorkspaceEvaluationPlanLevelSettings,
  formatLevelStepOptionLabel,
  getLevelDisplayLabelsForStep,
  parseEvaluationLevelDisplayLabelsByStepCount,
} from '../../types/evaluationPlan';
import EvaluationPlanLevelLabelFields from './EvaluationPlanLevelLabelFields';

const EvaluationPlanWorkspaceLevelSettingsModal: React.FC<{
  open: boolean;
  initialSettings: WorkspaceEvaluationPlanLevelSettings;
  saving?: boolean;
  onClose: () => void;
  onSave: (settings: WorkspaceEvaluationPlanLevelSettings) => void | Promise<void>;
}> = ({ open, initialSettings, saving = false, onClose, onSave }) => {
  const [activeStep, setActiveStep] = useState<EvaluationLevelStepCount>(5);
  const [defaultLevelStepCount, setDefaultLevelStepCount] =
    useState<EvaluationLevelStepCount>(5);
  const [draft, setDraft] = useState<EvaluationLevelDisplayLabelsByStepCount>(() =>
    emptyWorkspaceEvaluationPlanLevelSettings().labelsByStep
  );

  useEffect(() => {
    if (!open) return;
    const next = {
      defaultLevelStepCount: clampLevelStepCount(initialSettings.defaultLevelStepCount),
      labelsByStep: parseEvaluationLevelDisplayLabelsByStepCount(initialSettings.labelsByStep),
    };
    setDraft(next.labelsByStep);
    setDefaultLevelStepCount(next.defaultLevelStepCount);
    setActiveStep(next.defaultLevelStepCount);
  }, [open, initialSettings]);

  if (!open) return null;

  const patchStepLabels = (
    step: EvaluationLevelStepCount,
    labels: EvaluationLevelDisplayLabelsByStepCount[EvaluationLevelStepCount]
  ) => {
    setDraft((prev) => ({ ...prev, [step]: labels }));
  };

  return (
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        className="cp-modal cp-evaluation-plan-level-settings-modal"
        role="dialog"
        aria-modal="true"
        data-tutorial-ep="level-settings-modal"
        aria-labelledby="cp-evaluation-plan-level-settings-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="cp-evaluation-plan-level-settings-title">성취수준 표시 설정</h3>
        <p className="cp-evaluation-plan-level-settings-hint" role="status">
          단계별 표시 라벨과 신규 카테고리의 기본 단계를 설정합니다. 평가계획 생성 시 기본 단계는 카테고리 설정을 따르며, 각 평가계획에서 바꿀 수 있습니다.
        </p>
        <div className="cp-modal-body">
          <label className="cp-evaluation-plan-field">
            <span>기본 성취수준 단계</span>
            <select
              value={defaultLevelStepCount}
              onChange={(e) => setDefaultLevelStepCount(clampLevelStepCount(Number(e.target.value)))}
              aria-label="기본 성취수준 단계"
            >
              {LEVEL_STEP_COUNT_OPTIONS.map((count) => (
                <option key={count} value={count}>
                  {formatLevelStepOptionLabel(count, getLevelDisplayLabelsForStep(draft, count))}
                </option>
              ))}
            </select>
          </label>
          <div className="cp-evaluation-plan-level-settings-step-tabs" role="tablist" aria-label="성취수준 단계별 라벨">
            {LEVEL_STEP_COUNT_OPTIONS.map((step) => (
              <button
                key={step}
                type="button"
                role="tab"
                aria-selected={activeStep === step}
                className={`cp-evaluation-plan-level-settings-step-tab${activeStep === step ? ' is-active' : ''}`}
                onClick={() => setActiveStep(step)}
              >
                {step}단계
              </button>
            ))}
          </div>
          <p className="cp-evaluation-plan-level-settings-step-preview" role="status">
            미리보기: {formatLevelStepOptionLabel(activeStep, draft[activeStep])}
          </p>
          <EvaluationPlanLevelLabelFields
            levelStep={activeStep}
            displayLabels={draft[activeStep]}
            onDisplayLabelsChange={(labels) => patchStepLabels(activeStep, labels)}
          />
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose} disabled={saving}>
            취소
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            disabled={saving}
            onClick={() =>
              void onSave({
                defaultLevelStepCount,
                labelsByStep: parseEvaluationLevelDisplayLabelsByStepCount(draft),
              })
            }
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvaluationPlanWorkspaceLevelSettingsModal;
